import { useState } from 'react';
import BookingCalendar from './BookingCalendar';

export default function ContactSection() {
  const [activeTab, setActiveTab] = useState<'book' | 'contact'>('book');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-stone-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-light text-stone-900 mb-4">
            Schedule Your Consultation or Register as a Freelance Lawyer
          </h2>
          <p className="text-lg text-stone-600 font-light">
            Professional legal advice tailored to your specific needs.
          </p>
        </div>

        {/* Tab switcher */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex bg-stone-200 rounded-full p-1">
            <button
              onClick={() => setActiveTab('book')}
              className={`px-6 py-2 rounded-full text-sm font-light transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'book'
                  ? 'bg-stone-800 text-white shadow-sm'
                  : 'text-stone-600 hover:text-stone-800'
              }`}
            >
              <i className="ri-calendar-check-line mr-2"></i>Book Appointment
            </button>
            <button
              onClick={() => setActiveTab('contact')}
              className={`px-6 py-2 rounded-full text-sm font-light transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'contact'
                  ? 'bg-stone-800 text-white shadow-sm'
                  : 'text-stone-600 hover:text-stone-800'
              }`}
            >
              <i className="ri-mail-send-line mr-2"></i>Contact / Register
            </button>
          </div>
        </div>

        {/* Book Appointment tab */}
        {activeTab === 'book' && (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            <div className="lg:col-span-3">
              <BookingCalendar />
            </div>
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h3 className="text-lg font-medium text-stone-900 mb-4">Contact Information</h3>
                <div className="space-y-3 text-stone-600 font-light">
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-phone-line text-stone-500"></i>
                    </div>
                    <span>+(244) 923 502 981</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-mail-line text-stone-500"></i>
                    </div>
                    <span>paula.lopes@legal-fin.com</span>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium text-stone-900 mb-4">Office Hours</h3>
                <div className="space-y-2 text-stone-600 font-light">
                  <p>Monday – Friday: 9:00 AM – 5:00 PM</p>
                  <p>Saturday – Sunday: Closed</p>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium text-stone-900 mb-4">Languages</h3>
                <div className="space-y-2 text-stone-600 font-light">
                  <p>Portuguese</p>
                  <p>English</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Contact / Register tab */}
        {activeTab === 'contact' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div className="bg-white p-8 rounded-lg shadow-sm border border-stone-200">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <input
                    type="text"
                    name="name"
                    placeholder="Full Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-0 py-3 text-stone-900 placeholder-stone-500 border-0 border-b border-stone-300 focus:border-stone-700 focus:outline-none bg-transparent font-light"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-0 py-3 text-stone-900 placeholder-stone-500 border-0 border-b border-stone-300 focus:border-stone-700 focus:outline-none bg-transparent font-light"
                  />
                </div>
                <div>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-0 py-3 text-stone-900 placeholder-stone-500 border-0 border-b border-stone-300 focus:border-stone-700 focus:outline-none bg-transparent font-light"
                  />
                </div>
                <div>
                  <textarea
                    name="message"
                    rows={4}
                    placeholder="Describe your legal matter"
                    value={formData.message}
                    onChange={handleInputChange}
                    maxLength={500}
                    className="w-full px-0 py-3 text-stone-900 placeholder-stone-500 border-0 border-b border-stone-300 focus:border-stone-700 focus:outline-none bg-transparent resize-none font-light"
                  />
                  <div className="text-right text-xs text-stone-400 mt-1">
                    {formData.message.length}/500
                  </div>
                </div>
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-stone-800 text-stone-50 px-8 py-4 text-lg font-medium hover:bg-stone-900 transition-colors whitespace-nowrap cursor-pointer rounded-full disabled:opacity-50"
                  >
                    {isSubmitting ? 'Sending...' : 'Request Consultation / Register'}
                  </button>
                </div>
                {submitStatus === 'success' && (
                  <div className="text-green-600 text-sm font-light">
                    Thank you! We'll contact you within 24 hours.
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="text-red-600 text-sm font-light">
                    Something went wrong. Please try again.
                  </div>
                )}
              </form>
            </div>

            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-medium text-stone-900 mb-4">Contact Information</h3>
                <div className="space-y-3 text-stone-600 font-light">
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-phone-line text-stone-500"></i>
                    </div>
                    <span>+(244) 923 502 981</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-mail-line text-stone-500"></i>
                    </div>
                    <span>paula.lopes@legal-fin.com</span>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium text-stone-900 mb-4">Office Hours</h3>
                <div className="space-y-2 text-stone-600 font-light">
                  <p>Monday – Friday: 9:00 AM – 5:00 PM</p>
                  <p>Saturday – Sunday: Closed</p>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium text-stone-900 mb-4">Languages</h3>
                <div className="space-y-2 text-stone-600 font-light">
                  <p>Portuguese</p>
                  <p>English</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
